/**
 * MessageAssistant · AI 助手消息气泡（v4）
 *
 * 渲染顺序（v5）：
 *   1. 头像 + 角色名
 *   2. ToolCallsSummary 折叠头 · 单一控件
 *      · streaming → head 显示当前进度（message.text override / 当前工具 / elapsed 兜底）
 *                    chevron 收起完整时间轴；点开展开
 *      · done      → head 显示「已思考 X 秒」 · 默认收起 · 点开展开
 *      · streaming + 无 toolCalls + 无 message.text → 只显示 head，无 chevron
 *      · done + 无 toolCalls → 不渲染（finalText 担纲）
 *   3. done 时的 finalText（终态人话）
 *   4. 出错时显示 errorMessage
 *   5. payload.card 按 kind 分发到对应卡片：
 *      · folder_plan         → FolderPlanPreview (live / history 共用)
 *      · next_level / ask    → ChoiceOptionsCard
 *      · delete_confirm      → DeleteConfirmCard
 *      · delete_done         → DeleteDoneCard
 *      · build_task_status   → BuildTaskStatusMessage
 *
 * 卡片不带「确认 / 选项」按钮 — 那些通过 bottom_options 在 Composer 底部统一渲染。
 *
 * 为什么 streaming 不展示 raw text
 *  · 后端 ai-folder-classifier 的 llm_delta 是 LLM 原始输出，可能是 JSON 字符串（如
 *    `{"action":"final_proposal",...}`），暴露给用户既丑又破坏体验。
 *  · 协议约定：done 事件的 message 字段才是给用户看的"人话"。
 *  · 所以 streaming 期间统一渲染为 loading；done 时再把 message 显示出来。
 */

import React from 'react'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useAppSelector } from '../../../../../../app/hooks'
import {
  selectAnswerForMessage,
  selectChainSessionId,
  selectIsMessageAnswered,
  selectSessionStatus
} from '../../../../../../slices/aiClassify'
import type { AssistantMessage } from '../../../../../../types/ai-classify'
import { useChatActions } from '../../ChatActionsContext'
import ChoiceOptionsCard from './cards/ChoiceOptionsCard'
import DeleteConfirmCard from './cards/DeleteConfirmCard'
import DeleteDoneCard from './cards/DeleteDoneCard'
import ToolCallsSummary from './cards/ToolCallsSummary'
import MessageFeedback from './MessageFeedback'
import FolderPlanPreview from '../../../FolderPlanPreview'
import BuildTaskStatusMessage from './BuildTaskStatusMessage'
import SparkleIcon from '../../../SparkleIcon'
import styles from './MessageAssistant.module.scss'

export interface MessageAssistantProps {
  message: AssistantMessage
}

const MessageAssistant: React.FC<MessageAssistantProps> = ({ message }) => {
  const isStreaming = message.status === 'streaming'
  const isError = message.status === 'error'

  // 空壳消息守卫: done 态且 text / card / toolCalls 三者皆空 → 不渲染
  //
  // 典型场景: 纯维度操作对话轮次中, 后端 done 事件 message='' 且无卡片 /
  // 无工具调用, 渲染会出现一个只有头像 + "AI 助手"文字的空白行, 误导用户
  // 以为 AI 没反应。
  //
  // streaming 态不在守卫范围内 (此时正在"AI 在思考"); error 态有 errorMessage
  // 要展示, 不算空。
  const hasCard = !!message.payload?.card
  const hasToolCalls = (message.payload?.toolCalls?.length ?? 0) > 0
  const hasText = message.text.length > 0
  if (!isStreaming && !isError && !hasCard && !hasToolCalls && !hasText) {
    return null
  }

  return (
    <div className={styles.message}>
      <div className={styles.header}>
        <div className={styles.avatar} aria-hidden>
          <SparkleIcon size={14} />
        </div>
        <span className={styles.roleName}>AI 助手</span>
      </div>

      {/* 思维链卡片 · v5 收成单条折叠头部
          streaming → head 显示当前进度 + 折叠 chevron
                     · message.text 优先作 override（如 "正在创建相册…"）
                     · 否则 ToolCallsSummary 内部按 toolCalls / elapsed 推断
          done → head 显示「已思考 X 秒」 · 默认收起 · 点 head 展开时间轴
          · streaming 时即使 toolCalls 为空也渲染（让 head 表达"AI 在工作"）
          · done 时 toolCalls 为空 ToolCallsSummary 内部 return null（finalText 担纲） */}
      {(isStreaming ||
        (message.payload?.toolCalls &&
          message.payload.toolCalls.length > 0)) && (
        <ToolCallsSummary
          calls={message.payload?.toolCalls ?? []}
          streaming={isStreaming}
          messageStartedAt={message.createdAt}
          overrideStreamingText={isStreaming ? message.text : undefined}
        />
      )}

      {/* 卡片区域 */}

      {/* done 时显示 finalText · 但**有结构化卡片时不渲染**
          ──────────────────────────────────────────────────────────────
          finalText 来源是后端 SSE done 事件的 message 字段，对应 LLM
          输出"第一段人话回复"。但实测 LLM（特别是混合思考模型关 thinking
          后）容易把内心分析写进第一段（如"用户说"按拍摄人分类"，这是路径一
          §4.2..."），暴露给用户。

          因为 prompt 已调试好不可动，从渲染层兜底：有结构化卡片
          （final_proposal / next_level / ask / delete_confirm /
          delete_done / build_task_status）时
          完全不展示 finalText —— 卡片自身的 summary/question 已经给
          用户看了，finalText 是冗余的，遮掉就行。

          仅在**无卡片且 finalText 非空**的纯文字回复场景才展示（罕见，
          兜底覆盖未来扩展）。 */}
      {!isStreaming &&
        !isError &&
        message.text.length > 0 &&
        !message.payload?.card && (
          <div className={styles.content}>{message.text}</div>
        )}

      {isError && (
        <div className={styles.errorContent}>
          <ExclamationCircleOutlined
            style={{ marginRight: 6, color: '#f5222d' }}
          />
          {message.errorMessage || '请求失败，请稍后重试'}
        </div>
      )}

      {message.payload?.card && <CardSwitch message={message} />}

      {/* 反馈点赞 / 点踩 · 仅 done 终态展示，streaming / error 时不渲染
          · 缺 backendMessageId 时组件内部自行 return null（不破坏布局） */}
      {!isStreaming && !isError && <MessageFeedback message={message} />}
    </div>
  )
}

const CardSwitch: React.FC<{ message: AssistantMessage }> = ({ message }) => {
  const card = message.payload!.card!
  const { sendUserMessage } = useChatActions()
  const sessionStatus = useAppSelector(selectSessionStatus)
  // 这条消息之后是否已经有 user 消息 = 用户已经回答过 → 锁死整条卡片的 options
  const isAnswered = useAppSelector(selectIsMessageAnswered(message.id))
  // 用户回答的内容（即下一条 user TextMessage 的 content）· 用于折叠未选项时识别选了哪条
  const answerText = useAppSelector(selectAnswerForMessage(message.id))
  // 自由选择 picker (2026-05-18) · ask 卡含 picker 时需要 sessionId 调 layer/values 接口
  const chainSessionId = useAppSelector(selectChainSessionId)
  const choiceDisabled = sessionStatus === 'streaming' || isAnswered

  switch (card.kind) {
    case 'folder_plan':
      // 文件夹方案预览 · live / history 统一卡片 (v6 重构)。
      // 每条触发了 dimension_updated 的 assistant message 持有自己的方案快
      // 照, 不再依赖 selection 单例 + anchor 选最后一条 — 多轮 refine / confirm
      // 后所有历史方案都按时序保留可回看, 跟 history 回看体验一致。
      return <FolderPlanPreview card={card} messageId={message.id} />
    case 'build_task_status':
      return <BuildTaskStatusMessage card={card} />
    case 'next_level':
    case 'ask':
      return (
        <ChoiceOptionsCard
          card={card}
          disabled={choiceDisabled}
          answerText={isAnswered ? answerText : null}
          onPick={(label) => sendUserMessage(label)}
          sessionId={chainSessionId || undefined}
        />
      )
    case 'delete_confirm':
      return <DeleteConfirmCard card={card} />
    case 'delete_done':
      return <DeleteDoneCard card={card} />
    default: {
      const _exhaustive: never = card
      return _exhaustive
    }
  }
}

export default MessageAssistant
